import { useContext, useEffect, useState } from 'react';
import { AdobeClickType, API } from '../../constants/constants';
import {
    GelScreenDetectorContext,
    GelRowLayout,
    GelContainerLite,
    GelParagraph,
    GelHeading5,
    GelHeading2,
    GelSpinner,
    GelForm,
    GelModal,
    GelButton,
    GelLabel,
    GelStandardDateInput,
    useGelFormData,
    GelTag,
    GelSelect,
    GelRadioGroup,
    GelRadio
} from '@tal-gel/components';
import { getGelTokens } from '@tal-gel/theming';
import styled from '@emotion/styled';
import axios from 'axios';
import { UserContext } from '../../common/usercontext/user.context';
import Card from '../../common/components/card';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import ResponsiveContainer from "../../components/responsive.container";
import { CorroItem } from './CorroItem';
import Flexbox from '../../common/components/FlexBox';
import Pagination from '../../common/components/Pagination';
import SESSION_STORAGE_KEY from '../../constants/storage.constant';
import { GET_LIST_OF_DIGICORRESPONDENCE_QUERY, GET_MEMBER_DIGICORRESPONDENCE_DOCUMENT_QUERY } from '../../graphql/queries/graphql-queries.constant';
import { DigiCorrespondence } from '../../common/models/memberclaims';
import { SaveFile } from '../../common/file.utils';
import { AdobeAnalytics } from '../../common/analytics/adobe-analytics';
import { useGetMessageHubDetailsContent } from '../../common/api/graphQLDataFetch';
import HorizontalRule from '../../common/components/HorizontalRule';
import { formatDate } from '../payments/helpers';


const MessageHubContainer = () => {
    const { global: { sizeBaseX15, sizeBaseX10, themeColorBackgroundLight, sizeBaseX3, sizeNone, sizeBaseUnit, 
        sizeBaseX5, sizeBaseX8, sizeBaseX4, sizeBaseX2 } } = getGelTokens();

    const { data, isFetched } = useGetMessageHubDetailsContent()

    const navigate = useNavigate();
    const { contextData, setUserContext } = useContext(UserContext)
    const [{ aid, rid, ssoid, tpid }] = useCookies(['aid', 'rid', 'ssoid', 'tpid']);
    const { screen, isXsScreen, isLargerThanLgScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;

    const [digiCorrespondenceLoaded, setDigiCorrespondenceLoaded] = useState(false);

    const [messages, setMessages] = useState<DigiCorrespondence[]>([]);
    const [pageNumber, setPageNumber] = useState(1);

    const messagesPerPage = 10;
    const pageCount = Math.ceil(messages?.length / messagesPerPage)
    
    const [isOpenModal, setOpenModal] = useState(false);
    const [benefitType, setBenefitType] = useState('');
    const [selectedBenefitType, setSelectedBenefitType] = useState('');
    const [documentType, setDocumentType] = useState('');
    const [selectedDocumentType, setSelectedDocumentType] = useState('');
    const [readStatus, setReadStatus] = useState('');
    const [selectedReadStatus, setSelectedReadStatus] = useState('');
    const [selectedDateRange, setSelectedDateRange] = useState({ from: '', to: '' });
    const [dateRange, setDateRange] = useState({ from: '', to: '' });
    const [showDateFilterTag, setShowDateFilterTag] = useState(false);
    const [showReadStatusFilterTag, setShowReadStatusFilterTag] = useState(false);
    const [showBenefitTypeFilterTag, setShowBenefitTypeFilterTag] = useState(false);
    const [showDocumentTypeFilterTag, setShowDocumentTypeFilterTag] = useState(false);
    const {
        onFormDataReset,
        resetForm
    } = useGelFormData({
        fromDate: '',
        toDate: ''
    });

    const resetSelectedFormData = () => {
        setSelectedBenefitType(data?.filter?.BenefitTypePlaceholder);
        setSelectedDocumentType(data?.filter?.DocumentTypePlaceholder);
        setSelectedReadStatus(data?.filter?.ReadStatusPlaceholder);
        setSelectedDateRange({ from: '', to: '' });
    }
    

    const resetFormData = () => {
        setBenefitType(data?.filter?.BenefitTypePlaceholder);
        setDocumentType(data?.filter?.DocumentTypePlaceholder);
        setReadStatus(data?.filter?.ReadStatusPlaceholder);
        setDateRange({ from: '', to: '' });
    }

    const onApplyClick = () => {
        setOpenModal(false);
        setBenefitType(selectedBenefitType);
        setDocumentType(selectedDocumentType);
        setReadStatus(selectedReadStatus);
        setDateRange(selectedDateRange)

        setShowDateFilterTag(!!(selectedDateRange.from && selectedDateRange.to));
        setShowReadStatusFilterTag(selectedReadStatus !== data?.filter?.ReadStatusPlaceholder);
        setShowBenefitTypeFilterTag(selectedBenefitType !== data?.filter?.BenefitTypePlaceholder);
        setShowDocumentTypeFilterTag(selectedDocumentType !== data?.filter?.DocumentTypePlaceholder);

        setMessages(messages.map(message => {
            let hideForFilter = false;

            if (selectedDateRange.from && selectedDateRange.to) {
                console.log('Filtering by date:', selectedDateRange.from, selectedDateRange.to);
                const from = new Date(selectedDateRange.from);
                const to = new Date(selectedDateRange.to);
                // Parse 'DD/MM/YYYY' format to Date
                const [day, month, year] = message.createdOn.split('/');
                const messageDate = new Date(`${year}-${month}-${day}`);

                if (!(messageDate >= from && messageDate <= to)) {
                    hideForFilter = true;
                }
            }

            if (selectedReadStatus === data?.filter?.ReadLabel && message.isRead === false) {
                hideForFilter = true;
            } else if (selectedReadStatus === data?.filter?.UnreadLabel && message.isRead === true) {
                hideForFilter = true;
            }

            if (selectedBenefitType !== data?.filter?.BenefitTypePlaceholder && `${message.claimType} - ${message.fineosClaimCaseNumber}` !== selectedBenefitType) {
                hideForFilter = true;
            }

            if (selectedDocumentType !== data?.filter?.DocumentTypePlaceholder && message.classification !== selectedDocumentType) {
                hideForFilter = true;
            }

            return { ...message, hideForFilter };
        }));
    }
    
    const onResetFilter = () => {
        resetSelectedFormData();       
    }

    const onClearAllFilter = () => {
        setShowDateFilterTag(false);
        setShowReadStatusFilterTag(false);
        setShowBenefitTypeFilterTag(false);        
        setShowDocumentTypeFilterTag(false);

        resetSelectedFormData(); 
        resetFormData(); 
        setMessages(messages.map(message => ({ ...message, hideForFilter: false })));
    }

    // Helper to check if a message should be hidden based on current filters
    const shouldHideMessage = (message: DigiCorrespondence, filters: { dateRange?: typeof dateRange, readStatus?: string, benefitType?: string, documentType?: string }) => {
        let hideForFilter = false;

        const {
            dateRange: dr = dateRange,
            readStatus: rs = readStatus,
            benefitType: bt = benefitType,
            documentType: dt = documentType
        } = filters;

        if (dr.from && dr.to) {
            const [day, month, year] = message.createdOn.split('/');
            const messageDate = new Date(`${year}-${month}-${day}`);
            const from = new Date(dr.from);
            const to = new Date(dr.to);
            if (!(messageDate >= from && messageDate <= to)) hideForFilter = true;
        }
        if (rs === data?.filter?.ReadLabel && message.isRead === false) hideForFilter = true;
        else if (rs === data?.filter?.UnreadLabel && message.isRead === true) hideForFilter = true;
        if (bt !== data?.filter?.BenefitTypePlaceholder && `${message.claimType} - ${message.fineosClaimCaseNumber}` !== bt) hideForFilter = true;
        if (dt !== data?.filter?.DocumentTypePlaceholder && message.classification !== dt) hideForFilter = true;

        return hideForFilter;
    };

    const updateMessagesWithFilters = (filters: Partial<{ dateRange: typeof dateRange, readStatus: string, benefitType: string, documentType: string }>) => {
        setMessages(messages.map(message => ({
            ...message,
            hideForFilter: shouldHideMessage(message, filters)
        })));
    };

    const onDateDeleteClick = () => {
        setShowDateFilterTag(false);
        setDateRange({ from: '', to: '' });
        updateMessagesWithFilters({ dateRange: { from: '', to: '' } });
    };

    const onReadStatusDeleteClick = () => {
        setShowReadStatusFilterTag(false);
        setReadStatus(data?.filter?.ReadStatusPlaceholder);
        updateMessagesWithFilters({ readStatus: data?.filter?.ReadStatusPlaceholder });
    };

    const onBenefitTypeDeleteClick = () => {
        setShowBenefitTypeFilterTag(false);
        setBenefitType(data?.filter?.BenefitTypePlaceholder);
        updateMessagesWithFilters({ benefitType: data?.filter?.BenefitTypePlaceholder });
    };

    const onDocumentTypeDeleteClick = () => {
        setShowDocumentTypeFilterTag(false);
        setDocumentType(data?.filter?.DocumentTypePlaceholder);
        updateMessagesWithFilters({ documentType: data?.filter?.DocumentTypePlaceholder });
    };

    const isValidResetFilter = () => (
        selectedDateRange.from || selectedDateRange.to ||
        selectedReadStatus !== data?.filter?.ReadStatusPlaceholder ||
        selectedBenefitType !== data?.filter?.BenefitTypePlaceholder ||
        selectedDocumentType !== data?.filter?.DocumentTypePlaceholder
    );

    const isValidApplyFilter = () => {
        const { from, to } = selectedDateRange;
        if ((from && !to) || (!from && to)) return false;
        if (from && to && new Date(from) > new Date(to)) return false;
        return (
            (from && to) ||
            selectedReadStatus !== data?.filter?.ReadStatusPlaceholder ||
            selectedBenefitType !== data?.filter?.BenefitTypePlaceholder ||
            selectedDocumentType !== data?.filter?.DocumentTypePlaceholder
        );
    }

    const onFilterClick = () => {
        setOpenModal(true);
        setSelectedBenefitType(benefitType !== undefined && benefitType !== '' ? benefitType : data?.filter?.BenefitTypePlaceholder);
        setSelectedDocumentType(documentType !== undefined && documentType !== '' ? documentType : data?.filter?.DocumentTypePlaceholder);
        setSelectedReadStatus(readStatus !== undefined && readStatus !== '' ? readStatus : data?.filter?.ReadStatusPlaceholder);
        setSelectedDateRange({ from: dateRange.from !== undefined && dateRange.from !== '' ? dateRange.from : '', to: dateRange.to !== undefined && dateRange.to !== '' ? dateRange.to : '' });
    }
    
    if (contextData.memberDetails?.displayLegalDisclosure)
        navigate("/yourDeclaration");

    useEffect(() => {
        setDigiCorrespondenceLoaded(false);

        // TODO: refactor this request
        axios.post(API.BASEURL, {
            query: GET_LIST_OF_DIGICORRESPONDENCE_QUERY,
        },
            {
                headers: {
                    'x-aid': aid,
                    'x-rid': rid,
                    ssoid,
                    tpid,
                    'sid': window.sessionStorage.getItem(SESSION_STORAGE_KEY.SESSIONID_TOKEN)
                }
            })
            .then(async (response) => {
                let digoCorroMessages = response.data.data.listDigiCorrespondences as DigiCorrespondence[];
                setMessages(digoCorroMessages);
                setDigiCorrespondenceLoaded(true);

                // Get number of unread DigiCorro messages
                let unreadDigiCorroMessages = digoCorroMessages.filter(message => message.isRead === false);

                setUserContext({
                    ...contextData,
                    memberDetails: {
                        ...contextData.memberDetails,
                        unReadDigiCorroMessageCount: unreadDigiCorroMessages.length
                    }
                });
            })
            .catch((error) => {
                console.log(error)
            })

        AdobeAnalytics.PushFormStepEvent(tpid)
        AdobeAnalytics.PushPageViewEvent(tpid)
     // eslint-disable-next-line react-hooks/exhaustive-deps        
    }, []);

    if (!isFetched || !digiCorrespondenceLoaded)
        return <GelSpinner medium overlay />

    const Spacer = styled.div(() => ({
        paddingTop: sizeBaseX5
    }));

    const PageHeader = styled.div(() => ({
        padding: isXsScreen(screen) ? `${sizeBaseX15}px ${sizeBaseX5}px` : `${sizeBaseX15}px ${sizeBaseX10}px`,
        backgroundColor: themeColorBackgroundLight
    }));

    const onCorroItemClick = (id: number) => {
        AdobeAnalytics.PushClickEvent(tpid, "Download Message", AdobeClickType.Button)

        console.log(`Downloading DigiCorrespondence document with id: ${id}, `);

        axios.post(API.BASEURL, {
            query: GET_MEMBER_DIGICORRESPONDENCE_DOCUMENT_QUERY,
            variables: {
                digiCorroId: id,
            }
        },
            {
                headers: {
                    'x-aid': aid,
                    'x-rid': rid,
                    ssoid,
                    tpid,
                    'sid': window.sessionStorage.getItem(SESSION_STORAGE_KEY.SESSIONID_TOKEN)
                }
            })
            .then(async (response) => {
                let selectedDigiCorroDoc = messages.find(message => message.digiCorroId === id);
                let content = response.data.data.downloadDigiCorrospondenceDocument.content;
                let fileName = response.data.data.downloadDigiCorrospondenceDocument.fileName;
                console.log(`Saving DigiCorrespondence document with id: ${id}, fileName: ${fileName}`);
                SaveFile(content, fileName);

                // Decrement unread DigiCorro message count if message hasnt been read
                if (selectedDigiCorroDoc?.isRead === false) {
                    let currentUnreadDigiCorroMessageCount = contextData?.memberDetails?.unReadDigiCorroMessageCount ?? 0;
                    setUserContext({
                        ...contextData,
                        memberDetails: {
                            ...contextData.memberDetails,
                            unReadDigiCorroMessageCount: currentUnreadDigiCorroMessageCount - 1
                        }
                    });
                    console.log(`Decremented unread DigiCorro message count(${currentUnreadDigiCorroMessageCount} -> ${currentUnreadDigiCorroMessageCount - 1})`);
                }

                // Mark document as read
                setMessages(messages.map(message =>
                    message.digiCorroId === id
                        ? { ...message, isRead: true }
                        : message
                ));
            })
            .catch((error) => {
                console.log(error)
            })
    };

    const getPaginatedMessages = (): DigiCorrespondence[] => {
        const startIndex = (pageNumber - 1) * messagesPerPage;
        const endIndex = startIndex + messagesPerPage;

        let filteredMessages = messages.filter(message => message.hideForFilter !== true);
        return filteredMessages.slice(startIndex, endIndex);
    };

    const displayMessages = () => {
        return messages?.length > 0
    }

    const getRemainderItems = () => Array.from({ length: messagesPerPage - getPaginatedMessages().length }, (_, index) => index)

    return (
        <>
            {isOpenModal &&
                <GelForm
                    labelAtTop
                    disableOnSubmit
                    preventSubmitOnInvalid
                    reset={resetForm}
                    onReset={onFormDataReset}
                    parseResponseBody={false}
                ><GelModal
                    width="sm"
                    height="auto"
                    open={isOpenModal}
                    onClose={() => setOpenModal(false)}
                    closeOnBackdropClick={false}
                    sticky
                    title={'Filter'}
                    action={
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <GelButton
                                name="resetFilter"
                                secondary medium
                                onClick={onResetFilter}
                                width={sizeBaseUnit * 22}
                                disabled={!isValidResetFilter()}

                            >
                                {data?.filter?.ResetLabel}
                            </GelButton>
                            <GelButton
                                name="applyFilter"
                                primary medium submit
                                width={sizeBaseUnit * 22}
                                onClick={onApplyClick}
                                disabled={
                                    !isValidApplyFilter() ||
                                    (
                                        (selectedDateRange.from || selectedDateRange.to) &&
                                        (!selectedDateRange.from || !selectedDateRange.to)
                                    )
                                }
                            >
                                {data?.filter?.ApplyLabel}
                            </GelButton>
                        </div>
                    }>


                        <>
                            <GelLabel>{data?.filter?.BenefitTypeLabel}</GelLabel>
                            <GelSelect
                                name="claimType"
                                value={selectedBenefitType}
                                onChange={event => setSelectedBenefitType(event.target.value)}                               
                                options={
                                    [
                                        {
                                            value: data?.filter?.BenefitTypePlaceholder,
                                            label: data?.filter?.BenefitTypePlaceholder
                                        },
                                        ...Array.from(
                                            new Set(
                                                messages
                                                    .filter(msg => msg.claimType && msg.fineosClaimCaseNumber)
                                                    .map(msg => `${msg.claimType} - ${msg.fineosClaimCaseNumber}`)
                                            )
                                        )
                                        .filter(option => option !== data?.filter?.BenefitTypePlaceholder)
                                        .map(option => ({
                                            value: option,
                                            label: option
                                        }))
                                    ]
                                }
                            />

                            <Spacer></Spacer>

                            <GelLabel>{data?.filter?.DocumentTypeLabel}</GelLabel>
                            <GelSelect
                                name="documentType"
                                value={selectedDocumentType}
                                onChange={event => setSelectedDocumentType(event.target.value)}
                                options={
                                    [
                                        {
                                            value: data?.filter?.DocumentTypePlaceholder,
                                            label: data?.filter?.DocumentTypePlaceholder
                                        },
                                        ...Array.from(
                                         new Set(
                                        messages
                                            .filter(msg => msg.classification) 
                                            .map(msg => msg.classification)   
                                    )
                                )
                                .filter(option => option !== data?.filter?.DocumentTypePlaceholder)
                                        .map(classification => ({
                                            value: classification,
                                            label: classification
                                        }))
                                    ]
                                }
                            />

                            <Spacer></Spacer>

                            <GelLabel>{data?.filter?.ReadStatusLabel}</GelLabel>
                            <GelRadioGroup
                                name="readStatus"
                                value={selectedReadStatus}
                                onChange={e => setSelectedReadStatus(e.target.value)}
                                gap="small"
                                style={{ paddingBottom: '20px' }}
                            >
                                <GelRadio value="All">{data?.filter?.ReadStatusPlaceholder}</GelRadio>
                                <GelRadio value="Read">{data?.filter?.ReadLabel}</GelRadio>
                                <GelRadio value="Unread">{data?.filter?.UnreadLabel}</GelRadio>
                            </GelRadioGroup>

                            <GelLabel style={{ paddingBottom: '24px' }}>{data?.filter?.DateLabel}</GelLabel>

                            <div style={{ display: 'flex', width: '100%', gap: '24px' }}>
                                <div style={{ flex: 1 }}>
                                    <GelParagraph>{data?.filter?.FromLabel}</GelParagraph>
                                    <GelStandardDateInput
                                        name="fromDate"
                                        value={selectedDateRange.from}
                                        onChange={ev => {
                                            setSelectedDateRange({ ...selectedDateRange, from: ev.target.value });
                                        }}
                                        
                                        required={!!selectedDateRange.to}
                                        errorMsg={{
                                            required: 'This field is required',
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <GelParagraph>{data?.filter?.ToLabel}</GelParagraph>
                                    <GelStandardDateInput
                                        name="toDate"
                                        value={selectedDateRange.to}
                                        onChange={ev => {
                                            setSelectedDateRange({ ...selectedDateRange, to: ev.target.value });
                                        }}
                                        required={!!selectedDateRange.from}
                                        errorMsg={{
                                            required: 'This field is required',
                                        }}
                                    />
                                </div>
                            </div>
                        </></GelModal>
                </GelForm>}

            <GelContainerLite style={{ padding: sizeNone, overflow: "hidden" }}>
                <PageHeader >
                    <GelHeading2>{data?.pageHeader}</GelHeading2>
                    <GelParagraph style={{ paddingTop: sizeBaseX3 }}>{data?.pageSubtext}</GelParagraph>
                </PageHeader>

                <ResponsiveContainer>
                    <GelRowLayout style={{ padding: isXsScreen(screen) ? `${sizeBaseX10}px ${sizeBaseX5}px` : `${sizeBaseX5}px ${sizeBaseX10}px` }}>
                        <Card title={data?.correspondenceHeader}
                            subTitle={data?.correspondenceSubText}
                            showFooter={false}>
                            <GelContainerLite style={{ padding: `${sizeBaseX8}px ${sizeBaseX4}px` }}>
                                {displayMessages() && (
                                    <>
                                        <div style={{}}>
                                            {showDateFilterTag && (
                                                <GelTag
                                                    tagId="1"
                                                    onDelete={onDateDeleteClick}
                                                    small
                                                    icon="ExitSmall"
                                                >
                                                    {`${formatDate(dateRange.from)} to ${formatDate(dateRange.to)}`}
                                                </GelTag>
                                            )}
                                            {showReadStatusFilterTag && (
                                                <GelTag
                                                    tagId="1"
                                                    onDelete={onReadStatusDeleteClick}
                                                    small
                                                    icon="ExitSmall"
                                                >                                                    
                                                    {readStatus}
                                                </GelTag>
                                            )}

                                            {showBenefitTypeFilterTag && (
                                                <GelTag
                                                    tagId="1"
                                                    onDelete={onBenefitTypeDeleteClick}
                                                    small
                                                    icon="ExitSmall"
                                                >                                                    
                                                    {benefitType}
                                                </GelTag>
                                            )}
                                            {showDocumentTypeFilterTag && (
                                                <GelTag
                                                    tagId="1"
                                                    onDelete={onDocumentTypeDeleteClick}
                                                    small
                                                    icon="ExitSmall"
                                                >                                                    
                                                    {documentType}
                                                </GelTag>
                                            )}

                                            <div style={{
                                                display: 'flex', width: isLargerThanLgScreen(screen) ? 'auto' : '100%', float: 'right', paddingBottom: sizeBaseX2 * 3, gap: '16px',
                                                paddingTop: isLargerThanLgScreen(screen) ? 'none' : sizeBaseX3
                                            }}>
                                                <GelButton
                                                    primary medium
                                                    disabled={!displayMessages()}
                                                    iconRight="FilterFunnel"
                                                    onClick={onFilterClick}
                                                    style={{ flex: 1 }}
                                                >{data?.filter?.FilterLabel}</GelButton>

                                                {(showDateFilterTag || showBenefitTypeFilterTag || showDocumentTypeFilterTag || showReadStatusFilterTag) && (
                                                    <GelButton
                                                        tertiary medium
                                                        onClick={onClearAllFilter}
                                                    >{data?.filter?.ClearAllLabel}</GelButton>
                                                )}
                                            </div>
                                        </div>

                                        <HorizontalRule />
                                        <Spacer></Spacer>
                                    </>
                                )}


                                <Flexbox gap={sizeBaseX2} flexDirection="column" style={messages.length === 0 ? { height: '384px' } : {}}>
                                    {messages.length > 0 && getPaginatedMessages().map(message =>
                                        <CorroItem
                                            key={message.digiCorroId}
                                            date={message.createdOn}
                                            text={`${message.fineosClaimCaseNumber} ${message.claimType}`}
                                            title={message.classification}
                                            variant={message.isRead ? "default" : "unread"}
                                            onClick={() => onCorroItemClick(message.digiCorroId)}
                                        />
                                        )}

                                    {messages.length > 0 && getRemainderItems().map(idx => <CorroItem key={`space-digicorro-${idx}`} variant={"hidden"} />)}

                                    {messages.length === 0 && <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%, -50%)", width: "100%" }}>
                                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                                            <GelHeading5 style={{ paddingTop: "10%", color: "#54544C" }}>{"No Results"}</GelHeading5>
                                            <GelParagraph style={{ paddingTop: "8px", color: "#54544C", fontWeight: "400" }}>{"You have no correspondence available"}</GelParagraph>
                                        </div>
                                    </div>}
                                </Flexbox>

                                {messages.length > 0 &&
                                    <>
                                        <Spacer />
                                        <Pagination page={pageNumber} count={pageCount} onChange={setPageNumber} />
                                    </>
                                }

                            </GelContainerLite>
                        </Card>
                    </GelRowLayout>
                </ResponsiveContainer>

            </GelContainerLite>
        </>
    );
};

export default MessageHubContainer;